import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { publicRoutes } from "router/routes"

import * as AuthApi from "features/auth/api/AuthApi"
import { useForm, useAlert, useOperator } from "hooks"
import { useForgotPasswordProcess } from "../hooks/useForgotPasswordProcess"
import { ErrorResponse } from "models"

import PublicLayout from "components/Layout/PublicLayout"
import { formStatusEnum, FormElement } from "components/Form"
import { Button, Markdown } from "components/Elements"
import { Seo } from "components/Seo"
import { WavesBicycode } from "operators/bicycode/components"
import { BICYCODE_ID } from "operators/bicycode/config"

export const ValidateForgotPasswordPage: React.FC = () => {
	const { forgotPasswordState } = useForgotPasswordProcess()

	const {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields: forgotPasswordState?.data,
	})

	const { t } = useTranslation()
	const navigate = useNavigate()
	const { alert } = useAlert()
	const { currentOperator } = useOperator()

	const isLoading = formStatus === formStatusEnum.loading

	const resendCode = async () => {
		try {
			setFormStatus(formStatusEnum.loading)

			const { getError } = await AuthApi.signInInitPassword(
				formFields.mail,
				currentOperator?.id
			)
			if (getError()) throw getError()
			alert("success", t("code_resend", { ns: "form" }))
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
		} finally {
			setFormStatus(formStatusEnum.ready)
			setFormFields((s: any) => ({
				...s,
				code: "",
			}))
		}
	}

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				setFormStatus(formStatusEnum.loading)

				const { getError } = await AuthApi.signInResetPassword(
					formFields.mail,
					currentOperator?.id,
					formFields.code,
					formFields.newpassword
				)

				if (getError()) throw getError()
				alert("success", t("success_password", { ns: "form" }))
				navigate(publicRoutes.signin.path)
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<PublicLayout>
			{currentOperator && (
				<>
					<div className="max-w-screen-3xl mx-auto flex flex-col flex-1 justify-center">
						<Seo
							title={t("forgotpassword_title", {
								ns: "seo",
								operator: currentOperator.name,
							})}
						/>
						{currentOperator.id === BICYCODE_ID && <WavesBicycode />}

						{forgotPasswordState?.data && (
							<div className="flex flex-col justify-center relative py-20">
								<div className="text-center">
									<h1 className="c-h1 text-primary mb-8">
										{t("forgot_password.title_2", {
											ns: "auth",
										})}
									</h1>
									<div className="max-w-4xl text-lg mx-auto mb-10">
										<Markdown>
											{t("forgot_password.received_code_description", {
												ns: "auth",
												mail: formFields.mail,
											})}
										</Markdown>
									</div>
								</div>
								<form className="mx-auto w-full md:px-12" autoComplete="off">
									<h3 className="font-bold text-primary mb-0">
										{t("signup.received_code_title", {
											ns: "auth",
										})}
									</h3>
									<div className="grid justify-start">
										<FormElement
											{...formElementCommonProps}
											value={formFields.code}
											name="code"
											type="code"
											id="code"
											className="mb-2 mx-auto"
											required={true}
										/>

										<button
											type="button"
											onClick={resendCode}
											className="underline text-xxs mb-6 text-primary justify-self-end"
										>
											{t("resend_code", {
												ns: "user",
											})}
										</button>
									</div>

									<h3 className="font-bold mb-2 text-primary">
										{t("forgot_password.modify_password", {
											ns: "auth",
										})}
									</h3>

									<FormElement
										{...formElementCommonProps}
										value={formFields.newpassword}
										name="newpassword"
										type="password"
										id="password"
										label={t("new_password", {
											ns: "user",
										})}
										className="mb-3"
										required={true}
										autocomplete="new-password"
									/>

									<FormElement
										{...formElementCommonProps}
										value={formFields.confirmpassword}
										name="confirmpassword"
										type="password"
										id="confirmPassword"
										label={t("confirm_password", {
											ns: "user",
										})}
										className=""
										required={true}
										passwordToConfirm={formFields.newpassword}
										autocomplete="new-password"
									/>

									<div className="flex items-center mt-12 justify-center">
										<Button
											variant="secondary"
											type="submit"
											onClick={handleSubmit}
											className="mx-1 px-10 py-1 lg:mx-3"
											isLoading={isLoading}
										>
											{t("global.validate")}
										</Button>

										<Button
											variant="secondaryBordered"
											type="button"
											onClick={() => {
												navigate(publicRoutes.signin.path)
											}}
											className="mx-1 px-10 py-1 lg:mx-3"
										>
											{t("global.cancel")}
										</Button>
									</div>
								</form>
							</div>
						)}
					</div>
				</>
			)}
		</PublicLayout>
	)
}
